/*
 * Mandatory Small Order Handling Fee
 *
 * Configure the numeric fee variant ID in Theme settings > Cart >
 * Small Order Handling Fee variant ID. Thresholds and Shopify cart money
 * values are expressed in cents.
 */
(function () {
  const config = window.smallOrderFeeConfig || {};
  const threshold = Number(config.threshold) || 10000;
  const variantId = Number.parseInt(config.variantId, 10) || 0;
  const feeProperty = '_small_order_handling_fee';
  let activeSync = null;
  let refreshRequested = false;
  let syncVersion = 0;
  let observerScheduled = false;
  const checkoutBypass = new WeakSet();

  function cartEndpoint(url) {
    return url || '/cart.js';
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      const error = new Error(`Small order fee request failed (${response.status})`);
      error.response = response;
      throw error;
    }

    return response.json();
  }

  function getCart() {
    return request(cartEndpoint(config.cartUrl));
  }

  function merchandiseSubtotal(cart) {
    return cart.items.reduce((total, item) => {
      return Number(item.variant_id) === variantId ? total : total + Number(item.final_line_price || 0);
    }, 0);
  }

  function feeLines(cart) {
    return cart.items.filter((item) => Number(item.variant_id) === variantId);
  }

  function formatMoney(cents) {
    const format = config.moneyFormat || '${{amount}}';
    const placeholder = format.match(/{{\s*(\w+)\s*}}/)?.[1] || 'amount';
    const value = Number(cents || 0);
    const withDelimiters = (number, precision, thousands, decimal) => {
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[0] + (parts[1] ? decimal + parts[1] : '');
    };
    const replacements = {
      amount: withDelimiters(value, 2, ',', '.'),
      amount_no_decimals: withDelimiters(value, 0, ',', '.'),
      amount_with_comma_separator: withDelimiters(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelimiters(value, 0, '.', ','),
      amount_with_apostrophe_separator: withDelimiters(value, 2, "'", '.'),
    };
    return format.replace(/{{\s*\w+\s*}}/, replacements[placeholder] || replacements.amount);
  }

  function updateMessages(cart) {
    if (!cart || !variantId) return;
    const subtotal = merchandiseSubtotal(cart);
    const hasMerchandise = subtotal > 0;
    const amountRemaining = Math.max(threshold - subtotal, 0);

    document.querySelectorAll('[data-small-order-fee-message]').forEach((message) => {
      message.hidden = !hasMerchandise;
      message.dataset.merchandiseSubtotal = String(subtotal);
      const remaining = message.querySelector('[data-small-order-fee-remaining]');
      if (!remaining) return;
      remaining.hidden = !hasMerchandise || amountRemaining === 0;
      if (amountRemaining > 0) {
        remaining.textContent = `Add ${formatMoney(amountRemaining)} more to remove the $20 small-order handling fee.`;
      }
    });
  }

  function lockFeeControls(root = document) {
    if (!variantId) return;
    root.querySelectorAll(`[data-quantity-variant-id="${variantId}"]`).forEach((input) => {
      input.value = '1';
      input.min = '1';
      input.max = '1';
      input.readOnly = true;
      input.setAttribute('aria-readonly', 'true');
      const row = input.closest('.cart-item');
      row?.setAttribute('data-small-order-fee-line', 'true');
      row?.querySelectorAll('.quantity__button, cart-remove-button').forEach((control) => {
        control.hidden = true;
        control.setAttribute('aria-hidden', 'true');
      });
    });
  }

  function setCheckoutBusy(busy) {
    document.querySelectorAll('button[name="checkout"], .cart__checkout-button').forEach((button) => {
      if (busy) {
        button.dataset.smallOrderFeeWasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        if (button.dataset.smallOrderFeeWasDisabled !== 'true') button.disabled = false;
        delete button.dataset.smallOrderFeeWasDisabled;
        button.removeAttribute('aria-busy');
      }
    });
  }

  async function reconcile() {
    let cart = await getCart();
    const subtotal = variantId ? merchandiseSubtotal(cart) : cart.total_price;

    if (!variantId && subtotal > 0) {
      throw new Error('Small Order Handling Fee variant ID is not configured in Theme settings > Cart.');
    }

    if (!variantId) {
      return { cart, changed: false };
    }

    const lines = feeLines(cart);
    const feeRequired = subtotal < threshold;
    const updates = {};

    if (feeRequired) {
      lines.forEach((line, index) => {
        if (index === 0) {
          if (line.quantity !== 1) updates[line.key] = 1;
        } else {
          updates[line.key] = 0;
        }
      });
    } else {
      lines.forEach((line) => (updates[line.key] = 0));
    }

    let changed = Object.keys(updates).length > 0;
    if (changed) {
      await request(config.updateUrl || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }

    if (feeRequired && lines.length === 0) {
      changed = true;
      await request(config.addUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { [feeProperty]: 'true' } }],
        }),
      });
    }

    if (changed) cart = await getCart();
    updateMessages(cart);
    lockFeeControls();
    return { cart, changed };
  }

  async function refreshCartSections() {
    const sectionNames = ['cart-icon-bubble', 'cart-drawer'];
    const response = await fetch(`${window.routes?.cart_url || '/cart'}?sections=${sectionNames.join(',')}`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) return;
    const sections = await response.json();

    const icon = document.getElementById('shopify-section-cart-icon-bubble');
    if (icon && sections['cart-icon-bubble']) {
      const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      icon.innerHTML = parsed.querySelector('.shopify-section')?.innerHTML || icon.innerHTML;
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer && sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const replacement = parsed.querySelector('#CartDrawer .drawer__inner');
      const current = drawer.querySelector('#CartDrawer .drawer__inner');
      if (current && replacement) current.innerHTML = replacement.innerHTML;
    }
    drawer?.setHeaderCartIconAccessibility?.();
    lockFeeControls();
  }

  function sync(options = {}) {
    syncVersion += 1;
    refreshRequested = refreshRequested || Boolean(options.refresh);
    if (activeSync) return activeSync;

    setCheckoutBusy(true);
    activeSync = (async () => {
      let result;
      let completedVersion;
      let anyChanged = false;
      do {
        completedVersion = syncVersion;
        result = await reconcile();
        anyChanged = anyChanged || result.changed;
      } while (completedVersion !== syncVersion);

        if (anyChanged && window.location.pathname.replace(/\/$/, '') === (window.routes?.cart_url || '/cart').replace(/\/$/, '')) {
          window.location.reload();
          return result;
        }
        if (refreshRequested) await refreshCartSections();
        return result;
      })()
      .catch((error) => {
        console.error(error);
        throw error;
      })
      .finally(() => {
        activeSync = null;
        refreshRequested = false;
        setCheckoutBusy(false);
      });

    return activeSync;
  }

  function showCheckoutError(error) {
    const target = document.getElementById('cart-errors') || document.querySelector('[data-small-order-fee-message]');
    if (target) {
      target.textContent = 'Checkout is temporarily unavailable because the Small Order Handling Fee could not be verified. Please refresh and try again.';
      target.setAttribute('role', 'alert');
    }
    console.error(error);
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!variantId) return;
      const feeControl = event.target.closest(`[data-small-order-fee-line] .quantity__button, [data-small-order-fee-line] cart-remove-button`);
      if (feeControl) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (checkoutBypass.has(event.target)) {
        checkoutBypass.delete(event.target);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      sync({ refresh: true })
        .then(() => {
          checkoutBypass.add(event.target);
          event.target.requestSubmit(submitter);
        })
        .catch(showCheckoutError);
    },
    true
  );

  document.addEventListener('DOMContentLoaded', () => {
    lockFeeControls();
    sync({ refresh: false }).catch((error) => {
      if (document.querySelector('form[action*="/cart"] button[name="checkout"]')) showCheckoutError(error);
    });

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => sync({ refresh: true }));
    }

    new MutationObserver(() => {
      if (observerScheduled) return;
      observerScheduled = true;
      requestAnimationFrame(() => {
        observerScheduled = false;
        lockFeeControls();
      });
    }).observe(document.body, { childList: true, subtree: true });
  });

  window.addEventListener('pageshow', () => sync({ refresh: false }).catch(console.error));

  window.SmallOrderFee = { sync, lockFeeControls };
})();
